import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { X, Upload, Image as ImageIcon, Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Check for local optimizer URL (for development)
// When set, images are sent directly to localhost optimizer instead of Edge Function
const LOCAL_OPTIMIZER_URL = import.meta.env.VITE_LOCAL_OPTIMIZER_URL;

interface ImageResponse {
  url: string;
  path?: string;
  width: number;
  height?: number;
  format?: string;
}

interface OptimizeResponse {
  success?: boolean;
  images?: ImageResponse[];
  urls?: Record<string, string>;
  sizes?: Record<string, number>;
  message?: string;
  error?: string;
}

interface SiteImageUploadProps {
  imageType: 'hero' | 'stock' | 'exterior_render' | 'amenity';
  existingImageUrl?: string;
  existingImageUrls?: string[];
  onImageUpdated: (url: string) => void;
  onImagesUpdated?: (urls: string[]) => void;
  label?: string;
  description?: string;
  block?: 'A' | 'B'; // For exterior_render only
  imageNameSuffix?: string; // For exterior_render Block B multi-image (e.g. 'left', 'center', 'right')
  category?: string; // For stock images (e.g., '1bed', '2bed', 'amenity')
  maxImages?: number; // Default: 1 for hero/exterior, multiple for stock/amenity
  showWarning?: boolean;
  warningMessage?: string;
}

export function SiteImageUpload({
  imageType,
  existingImageUrl,
  existingImageUrls = [],
  onImageUpdated,
  onImagesUpdated,
  label,
  description,
  block,
  imageNameSuffix,
  category,
  maxImages,
  showWarning = false,
  warningMessage,
}: SiteImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();

  // Determine if multiple images are allowed
  const allowMultiple = imageType === 'stock' || imageType === 'amenity' || (maxImages && maxImages > 1);
  const effectiveMaxImages = maxImages || (imageType === 'stock' || imageType === 'amenity' ? 50 : 1);
  const currentImages = existingImageUrl ? [existingImageUrl] : existingImageUrls;
  const displayLabel = label || 
    (imageType === 'hero' ? 'Hero Image' : 
     imageType === 'stock' ? 'Stock Images' : 
     imageType === 'amenity' ? 'Amenity Images' :
     `Exterior Render${block ? ` (Block ${block})` : ''}`);

  const processFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    // Filter to only image files
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast({
        title: 'Invalid files',
        description: 'Please upload image files only',
        variant: 'destructive',
      });
      return;
    }

    if (currentImages.length + imageFiles.length > effectiveMaxImages) {
      toast({
        title: 'Too many images',
        description: `Maximum ${effectiveMaxImages} image(s) allowed`,
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const uploadedUrls: string[] = [];

      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        
        // Generate image name based on type
        let imageName: string;
        if (imageType === 'hero') {
          imageName = `hero-${Date.now()}`;
        } else if (imageType === 'exterior_render') {
          const base = `block-${block?.toLowerCase() || 'a'}-front`;
          imageName = imageNameSuffix ? `${base}-${imageNameSuffix}` : base;
        } else if (imageType === 'amenity') {
          const baseName = file.name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
          imageName = baseName;
        } else {
          // Stock image
          const baseName = file.name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
          imageName = category ? `${category}-${baseName}` : baseName;
        }

        // Log which optimizer mode we're using
        const optimizerMode = LOCAL_OPTIMIZER_URL ? 'local' : 'edge-function';
        console.log('Uploading site image:', { file: file.name, imageName, imageType, block, category, mode: optimizerMode });

        // Create FormData
        const formData = new FormData();
        formData.append('file', file);
        formData.append('unitId', 'site-images'); // Special identifier for site-wide images
        formData.append('imageType', imageType);
        formData.append('imageName', imageName);

        let data: OptimizeResponse;

        if (LOCAL_OPTIMIZER_URL) {
          // Use local optimizer directly in development
          console.log('Using local optimizer:', LOCAL_OPTIMIZER_URL);
          const response = await fetch(`${LOCAL_OPTIMIZER_URL}/optimize`, {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(errorData.error || `Local optimizer error: ${response.status}`);
          }

          data = await response.json();
        } else {
          // Call Edge Function to optimize and upload (production)
          // Use fetch directly to get better error details
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            throw new Error('Not authenticated');
          }

          const { url: supabaseUrl, anonKey } = await import('@/lib/env').then(m => m.getSupabaseConfig());
          const functionUrl = `${supabaseUrl}/functions/v1/optimize-unit-image`;
          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': anonKey,
            },
            body: formData,
          });

          let responseData;
          try {
            responseData = await response.json();
          } catch {
            const text = await response.text();
            throw new Error(`Failed to parse response: ${text}`);
          }

          if (!response.ok) {
            console.error('Upload error response:', responseData);
            const errorMessage = responseData?.error || responseData?.message || `HTTP ${response.status}: ${response.statusText}`;
            const errorDetails = responseData?.details ? `${errorMessage} - ${JSON.stringify(responseData.details)}` : errorMessage;
            throw new Error(errorDetails);
          }

          data = responseData;
        }

        console.log('Upload response:', data);

        // Store the primary URL (use the largest size as the default)
        if (data?.images && data.images.length > 0) {
          const largestImage = data.images.reduce((prev, curr) => 
            curr.width > prev.width ? curr : prev
          );
          
          if (largestImage?.url) {
            uploadedUrls.push(largestImage.url);
          }
        }

        setProgress(((i + 1) / imageFiles.length) * 100);
      }

      // Update parent component
      if (imageType === 'hero' || imageType === 'exterior_render') {
        // Single image update
        if (uploadedUrls.length > 0) {
          onImageUpdated(uploadedUrls[0]);
        }
      } else {
        // Multiple images update
        if (onImagesUpdated) {
          onImagesUpdated([...currentImages, ...uploadedUrls]);
        } else if (uploadedUrls.length > 0) {
          onImageUpdated(uploadedUrls[0]);
        }
      }

      toast({
        title: 'Success',
        description: `Uploaded ${imageFiles.length} image(s) successfully`,
      });
    } catch (error: unknown) {
      console.error('Upload error:', error);
      let errorMessage = 'Failed to upload images';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // Try to extract error from Supabase error object
        const err = error as any;
        errorMessage = err.message || err.error || err.details?.error || JSON.stringify(err);
      }
      
      toast({
        title: 'Upload failed',
        description: errorMessage,
        variant: 'destructive',
        duration: 10000, // Show longer so user can read it
      });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [imageType, block, category, currentImages, effectiveMaxImages, onImageUpdated, onImagesUpdated, toast]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await processFiles(files);
    // Reset the input
    e.target.value = '';
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading && currentImages.length < effectiveMaxImages) {
      setIsDragging(true);
    }
  }, [uploading, currentImages.length, effectiveMaxImages]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (uploading || currentImages.length >= effectiveMaxImages) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  }, [uploading, currentImages.length, effectiveMaxImages, processFiles]);

  const removeImage = (index: number) => {
    if (imageType === 'hero' || imageType === 'exterior_render') {
      onImageUpdated('');
    } else {
      const newImages = currentImages.filter((_, i) => i !== index);
      if (onImagesUpdated) {
        onImagesUpdated(newImages);
      }
    }
    
    toast({
      title: 'Image removed',
      description: 'Image removed successfully',
    });
  };

  const inputId = `site-image-upload-${imageType}${block ? `-${block}` : ''}${category ? `-${category}` : ''}`;

  return (
    <div className="space-y-4">
      {showWarning && warningMessage && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{warningMessage}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div>
          <Label>{displayLabel}</Label>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
          {currentImages.length > 0 && (
            <span className="text-sm text-muted-foreground ml-2">
              ({currentImages.length}/{effectiveMaxImages})
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading || currentImages.length >= effectiveMaxImages}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Upload
            </>
          )}
        </Button>
      </div>

      <Input
        id={inputId}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        multiple={allowMultiple}
        onChange={handleFileUpload}
        className="hidden"
      />

      {uploading && (
        <div className="space-y-2">
          <Progress value={progress} />
          <p className="text-sm text-muted-foreground text-center">
            Optimizing and uploading... {Math.round(progress)}%
          </p>
        </div>
      )}

      {/* Image Preview */}
      {currentImages.length > 0 && (
        <div className={`grid gap-4 ${imageType === 'stock' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'}`}>
          {currentImages.map((url, index) => (
            <div key={index} className="relative group">
              <img
                src={url}
                alt={`${displayLabel} ${index + 1}`}
                className={`w-full ${imageType === 'stock' ? 'h-32' : 'h-48'} object-cover rounded-lg border border-border`}
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => removeImage(index)}
              >
                <X className="h-4 w-4" />
              </Button>
              {imageType === 'hero' && index === 0 && (
                <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  Primary Hero
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors
          ${isDragging 
            ? 'border-primary bg-primary/10' 
            : currentImages.length === 0 
              ? 'border-border bg-muted/20' 
              : 'border-transparent bg-transparent'
          }
          ${!uploading && currentImages.length < effectiveMaxImages ? 'cursor-pointer hover:border-primary/50' : ''}
        `}
        onClick={() => {
          if (!uploading && currentImages.length < effectiveMaxImages) {
            document.getElementById(inputId)?.click();
          }
        }}
      >
        {currentImages.length === 0 ? (
          <>
            <ImageIcon className={`h-12 w-12 mb-2 transition-colors ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
            <p className={`text-sm transition-colors ${isDragging ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              {isDragging 
                ? 'Drop images here to upload' 
                : `Drag and drop ${displayLabel.toLowerCase()} here, or click to browse`}
            </p>
            {allowMultiple && (
              <p className="text-xs text-muted-foreground mt-1">
                You can upload multiple images at once
              </p>
            )}
          </>
        ) : (
          <>
            <ImageIcon className={`h-8 w-8 mb-2 transition-colors ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
            <p className={`text-xs transition-colors ${isDragging ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              {isDragging 
                ? 'Drop images here to add more' 
                : allowMultiple && currentImages.length < effectiveMaxImages
                  ? 'Drag and drop more images here, or click to browse'
                  : ''}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

